import dotProp from 'dot-prop-immutable'
import findIndex from 'lodash/findIndex'
import accounts, { ERROR_NEED_ADDITIONAL } from '@/api/accounts'
import deleteRecord from '@/utils/delete-record'
import updateRecord from '@/utils/update-record'
import rg4js from 'raygun4js'
import Vue from 'vue'

const getBaseState = () => ({
  list: [],
  adding: false,
  waiting: {},
  accountsRepartition: []
})

export default () => ({
  namespaced: true,

  state: getBaseState(),

  actions: {
    add (context, { bankId, fields, token, provider }) {
      context.commit('adding')
      return accounts.add(provider.bankId, fields, token, provider.host)
        .then(data => {
          if (data.error === ERROR_NEED_ADDITIONAL) {
            context.commit('done')
            const err = {
              response: {
                status: 400,
                data: {
                  code: ERROR_NEED_ADDITIONAL
                }
              },
              fields: data.fields
            }
            throw err
          }
          if (!data.error) {
            accounts.addLoading(data)
          }
          const list = context.state.list
          const accountId = list.length ? Number(list[list.length - 1].id) + 1 : 1
          const waitingData = {
            bank: {
              id: bankId
            },
            id: String(accountId),
            portfoliosCash: data.balance,
            contracts: Object.values(data.accounts).map(account => {
              return {
                amount: account.balance,
                kind: account.type,
                kindDetail: account.kindDetail,
                name: account.name,
                currency: account.currency.id
              }
            })
          }

          context.commit('add', waitingData)
          context.dispatch('done')

          const demoAccount = context.state.list.find(account => account.bank.name === 'Démonstration')
          if (demoAccount) {
            context.dispatch('delete', demoAccount.id)
          }

          return {
            data: data,
            id: accountId
          }
        })
        .catch(err => {
          const { response } = err
          if (response && response.data && response.data.code !== 'additionalInformationNeeded' &&
            response.data.code !== 'wrongpass') {
            rg4js('send', new Error(response.data.message || response.data.description))
          }
          context.commit('done')
          throw err
        })
    },

    updatePassword (context, { connexionId, password, token, provider }) {
      context.commit('adding')
      return accounts.updatePassword(connexionId, password, token, provider.host)
        .then(data => {
          if (data.error === ERROR_NEED_ADDITIONAL) {
            context.commit('done')
            const err = {
              response: {
                status: 400,
                data: {
                  code: ERROR_NEED_ADDITIONAL
                }
              },
              fields: data.fields
            }
            throw err
          }
          context.dispatch('done')
        })
        .catch(err => {
          const { response } = err
          if (response && response.data && response.data.code !== 'additionalInformationNeeded' &&
            response.data.code !== 'wrongpass') {
            rg4js.send(new Error(response.data.message || response.data.description))
          }
          context.commit('done')
          throw err
        })
    },

    delete (context, id) {
      return accounts.delete(id)
        .then(() => {
          context.dispatch('fetchRepartition')
          context.dispatch('contracts/fetchRepartition', null, { root: true })
          return context.commit('delete', id)
        })
    },

    done (context) {
      Vue.nextTick(() => context.commit('done'))
    },

    fetch (context, cached = true) {
      const { list } = context.state
      if (cached && list.length) {
        return Promise.resolve(list)
      }

      return accounts.fetch()
        .then(data => {
          context.commit('fetch', data)
        })
    },

    fetchById (context, id) {
      return accounts.fetchById(id)
        .then(data => {
          context.commit('fetchById', data)
        })
    },

    fetchRepartition (context, cache) {
      if (cache && context.state.accountsRepartition.length) {
        return Promise.resolve()
      }
      return accounts.fetchRepartition()
        .then(({ data }) => context.commit('fetchRepartition', data))
    },

    update (context, payload) {
      context.commit('update', payload)
    },

    synchronize ({ commit }, accountId) {
      return accounts.synchronize(accountId)
        .then(data => {
          commit('update', data)
        })
    },

    updateContract (context, { portfolioId, data, update = true }) {
      return accounts.updateContract(portfolioId, data)
        .then((response) => update && context.commit('updateContract', {
          accountId: response.accountId,
          portfolioId,
          data
        }))
    }
  },

  mutations: {
    adding (state) {
      state.adding = true
    },

    done (state) {
      state.adding = false
    },

    add (state, data) {
      state.waiting = data
    },

    fetch (state, data) {
      state.list = data
    },

    fetchById (state, data) {
      const index = findIndex(state.list, { id: data.id })
      if (index >= 0) {
        state.list = [
          ...state.list.slice(0, index),
          data,
          ...state.list.slice(index + 1)
        ]
      } else {
        state.list = [
          ...state.list,
          data
        ]
      }
    },

    fetchRepartition (state, data) {
      state.accountsRepartition = data
    },
    resetRepartition (state) {
      state.accountsRepartition = []
    },

    delete ({ list }, id) {
      deleteRecord(list, id)
    },

    update ({ list }, data) {
      updateRecord(list, data, false)
    },

    reset (state) {
      const baseState = getBaseState()
      Object.keys(baseState).forEach(key => {
        state[key] = baseState[key]
      })
    },
    updateContract (state, { accountId, portfolioId, data }) {
      const accountIndex = findIndex(state.list, { id: accountId })
      if (accountIndex > -1) {
        const portfolioIndex = findIndex(state.list[accountIndex].portfolios, { id: portfolioId })
        if (portfolioIndex > -1) {
          state.list = dotProp.merge(state.list, `${accountIndex}.portfolios.${portfolioIndex}`, data)
        }
      }
    }
  },

  getters: {
    getById ({ list }) {
      return id => list.find(account => account.id === id)
    },
    getPortfoliosByKind: ({ list }) => (kind) => {
      const portfolios = []
      for (const account of list) {
        portfolios.push(...(account.portfolios.filter(portfolio => portfolio.kind === kind && portfolio.status !== 'hidden')))
      }
      return portfolios
    }
  }
})
